// Del.js
// The Del object is the main object that contains all the data for the DEL form. We will import the different objects that contain the data for each section of the form, Projects, Employees, Units, Fields Notes, JSA, and Waste Disposal information.

import { DelProject } from "./DelProject";
import { DelEmployee } from "./DelEmployee";
import { DelUnit, MiscEquip, RentalEquip } from "./DelUnit";
import { DelProjectUnit } from "./DelProjectUnit";
import { FieldNotes } from "./FieldNotes";
import { DelClientSignatures } from "./DelClientSignatures";
import { DelSdsReference, normalizeProductUid } from "./DelSdsReference";
// JSA files import
import { JsaV1 } from "../Jsa/JsaV1";
import { JsaV2 } from "../Jsa/JsaV2";
// Waste Disposal files import
import { WasteDisposal } from "../WasteDisposal/WasteDisposal";
// Photos files import
import { FormPhotos } from "../FormPhotos/FormPhotos";
// Validation files import
import { validateDraftDel, validateDelSubmitted } from "./utils/delValidation";
import { collectValidationMessages } from "./utils/collectValidationMessages";
// Date utilities import
import { getLocalDateString } from "../../utils/dateUtils";

export class Del {
  constructor(data = {}) {
    this.id = data.id || null;
    this.approved = data.approved || false;
    this.del_creation_date = data.del_creation_date || null;
    this.del_submit_date = data.del_submit_date || null;
    this.username = data.username || null;
    this.user_id = data.user_id || null;
    this.user_full_name = data.user_full_name || null;
    this.approved_by_name = data.approved_by_name || null;
    this.canUnapprove = data.can_unapprove || true;
    // The project info is a DelProject object, and we will use the fromExistingDel method to convert the data from the database into a DelProject object, else we will create a new DelProject object.
    this.projectInfo = data ? new DelProject(data) : new DelProject();
    // The employees data is an array of DelEmployee objects
    this.employees = data.employees
      ? data.employees.map((employee) => new DelEmployee(employee))
      : [];
    // The units data is an array of DelUnit objects
    this.units = data.units ? data.units.map((unit) => new DelUnit(unit)) : [];
    // The misc units data is an array of MiscEquip objects
    this.miscUnits = data.misc_units
      ? data.misc_units.map((unit) => new MiscEquip(unit))
      : [];
    // The rental units data is an array of RentalEquip objects
    this.rentalUnits = data.misc_rentals
      ? data.misc_rentals.map((unit) => new RentalEquip(unit))
      : [];
    // The project-specific units data is an array of DelProjectUnit objects
    this.projectSpecificUnits = data.project_specific_units
      ? data.project_specific_units.map((unit) => new DelProjectUnit(unit))
      : [];
    // The field notes data is a FieldNotes object

    this.fieldNotes = data.field_notes
      ? new FieldNotes(data.field_notes)
      : new FieldNotes();

    this.clientSignatures = data.client_signatures
      ? data.client_signatures.map(
          (signature) => new DelClientSignatures(signature)
        )
      : [];

    // SDS attached from the 3E catalog (Misc > SDS); additive field, older cached DELs default to []
    this.sdsReferences = data.sds_references
      ? data.sds_references.map((reference) => new DelSdsReference(reference))
      : [];

    console.log("JSA Data:", data.jsa_version);
    // Default JSA version is 2, but we need to account for older JSA versions.
    if (this.projectInfo.jsaVersion === 2) {
      this.jsa = data.jsa ? new JsaV2(data.jsa) : new JsaV2();
    } else {
      console.log("JSA V1 Data:", data.jsa);
      this.jsa = data.jsa ? new JsaV1(data.jsa) : new JsaV1();
    }

    this.wasteDisposal = data.waste_disposal
      ? new WasteDisposal(data.waste_disposal)
      : new WasteDisposal();

    // Photos array - populated with presigned URLs from backend
    this.photos = data.photos
      ? data.photos.map((photo) => new FormPhotos(photo))
      : [];

    // Setting up a spot to store teh active tabs in our components when we go between them to keep the user experience consistent.
    // Setting the defaults to the first tab
    this.activeComponentTabs = {
      // UNITS_TAB: {
      //   ACTIVE_TAB: "All Units",
      //   ACTIVE_UNIT_VIEW: {
      //     TAB_SEARCH_UNITS: "all-units",
      //     TAB_NAME: "All Units",
      //   },
      //   ALL_UNITS_SEARCH: true,
      // },
      JSA_TAB: {
        ACTIVE_TAB: "All",
        SIGNATURE_TAB: "Employee Signatures",
      },
      WASTE_DISPOSAL_TAB: {
        ACTIVE_TAB: "All",
      },
    };

    // Setting errors so that these can be used to highlight parameters throughtout the application.
    this.errors = null;
  }

  // All add functions are used to save over the existing del data, so that the updated data is smoothly saved to the database.
  // addProjectInfo(newProjectInfo) {
  //   this.projectInfo = DelProject.fromExistingDel(newProjectInfo);
  // }

  // Updating the del_id in the delData
  updateDelId(delId) {
    this.id = delId;
  }

  addEmployeeInfo(newEmployeeInfo) {
    this.employees = newEmployeeInfo.map((employee) =>
      DelEmployee.fromExistingEmployee(employee)
    );
  }

  // Adding a blank new employee to the delData
  addNewBlankEmployee() {
    const newEmployee = new DelEmployee();
    this.employees.push(newEmployee);
    return newEmployee.employeeRowId;
  }

  // Removing an employee from the delData
  removeEmployee(employeeRowId) {
    this.employees = this.employees.filter(
      (employee) => employee.employeeRowId !== employeeRowId
    );
  }

  // Adding a new blank unit to the delData, with the unitType passed in as a parameter for the DelUnit constructor.
  addNewBlankUnit(unitType) {
    // Create a new DelUnit object and update the unitType
    const newUnit = new DelUnit();
    newUnit.updateUnitType(unitType);
    // Add the new unit to the units array
    this.units.push(newUnit);
    return newUnit.rowId;
  }

  // Removing a unit from the delData
  removeUnit(unitRowId) {
    this.units = this.units.filter((unit) => unit.rowId !== unitRowId);
  }

  // Adding a new blank misc unit to the delData
  addNewBlankMiscUnit() {
    this.miscUnits.push(new MiscEquip());
  }

  // Removing a misc unit from the delData
  removeMiscUnit(miscUnitRowId) {
    this.miscUnits = this.miscUnits.filter(
      (unit) => unit.rowId !== miscUnitRowId
    );
  }

  // Adding a new blank rental unit to the delData
  addNewBlankRentalUnit() {
    this.rentalUnits.push(new RentalEquip());
  }

  // Removing a rental unit from the delData
  removeRentalUnit(rentalUnitRowId) {
    this.rentalUnits = this.rentalUnits.filter(
      (unit) => unit.rowId !== rentalUnitRowId
    );
  }

  // Adding a new blank project-specific unit to the delData
  addNewBlankProjectUnit(unitTableName) {
    const newUnit = new DelProjectUnit();
    if (unitTableName && unitTableName !== "all") {
      newUnit.unitTableName = unitTableName;
    }
    this.projectSpecificUnits.push(newUnit);
    return newUnit.rowId;
  }

  // Removing a project-specific unit from the delData
  removeProjectUnit(rowId) {
    this.projectSpecificUnits = this.projectSpecificUnits.filter(
      (unit) => unit.rowId !== rowId
    );
  }

  // Adding a new blank client signature to the delData
  addNewBlankClientSignature() {
    const newSignature = new DelClientSignatures();
    this.clientSignatures.push(newSignature);
    return newSignature.rowId;
  }

  // Removing a client signature from the delData
  removeClientSignature(clientSignatureRowId) {
    this.clientSignatures = this.clientSignatures.filter(
      (signature) => signature.rowId !== clientSignatureRowId
    );
  }

  // Attaching an SDS reference (a DelSdsReference instance) to the delData
  addSdsReference(reference) {
    this.sdsReferences = [...(this.sdsReferences || []), reference];
    return reference.rowId;
  }

  // Removing an SDS reference from the delData
  removeSdsReference(rowId) {
    this.sdsReferences = (this.sdsReferences || []).filter(
      (reference) => reference.rowId !== rowId
    );
  }

  // Whether a 3E product is already attached to this DEL
  hasSdsReference(productUid) {
    const wanted = normalizeProductUid(productUid);
    if (!wanted) return false;
    return (this.sdsReferences || []).some(
      (reference) => reference.productUid === wanted
    );
  }

  // Setting up the auto add function for the JSA Training Component, pulling employees from the delData.employees array. to the delData.jsa.jsaTrainingEmployeesInfo array.
  autoAddEmployeesToJsaTrainingEmployeesInfo() {
    if (this.employees.length > 0) {
      this.jsa.jsaTrainingInfo.addDelEmployeesToJsaTrainingEmployeesInfo(
        this.employees
      );
    }
  }

  // Adding a blank employee to the JSA Training Employees Info array
  addNewBlankEmployeeToJsaTrainingEmployeesInfo() {
    this.jsa.jsaTrainingInfo.addBlankJsaTrainingEmployeesInfo();
  }

  // Removing an employee from the JSA Training Employees Info array
  removeEmployeeFromJsaTrainingEmployeesInfo(employeeRowId) {
    this.jsa.jsaTrainingInfo.removeJsaTrainingEmployeesInfoRow(employeeRowId);
  }

  // Setting the active tabs for the JSA tab
  setActiveJsaTabs(tab) {
    this.activeComponentTabs.JSA_TAB.ACTIVE_TAB = tab;
  }

  setActiveJsaSignatureTab(tab) {
    this.activeComponentTabs.JSA_TAB.SIGNATURE_TAB = tab;
  }

  // Setting the active tabs for the waste disposal tab
  setActiveWasteDisposalTabs(tab) {
    this.activeComponentTabs.WASTE_DISPOSAL_TAB.ACTIVE_TAB = tab;
  }

  // Getting user name and id from the local storage
  getUserInfo(userContext) {
    if (userContext) {
      return {
        username: userContext.username,
        userId: userContext.employee_id,
      };
    }
    // Fallback to empty values if no user context is provided
    return { username: null, userId: null };
  }

  // Helper function to check if the del data has validation errors,the power of recursion...
  hasValidationErrors(errors) {
    const checkErrors = (err) => {
      if (err == null) return false;
      if (typeof err === "string") {
        return err.trim() !== "";
      }
      if (Array.isArray(err)) {
        // Recursively check each item in the array
        return err.some((item) => checkErrors(item));
      }
      if (typeof err === "object") {
        // Recursively check all object keys
        return Object.keys(err).some((key) => checkErrors(err[key]));
      }
      return false;
    };

    return checkErrors(errors);
  }

  saveDraftDelData(delMode, userContext) {
    // Skip frontend validation for drafts to allow autosave with incomplete fields
    this.errors = null;
    // Getting user name and id from the provided user context
    const { username, userId } = this.getUserInfo(userContext);
    // Turning the del data into a json object
    const delData = {
      main_del_data: {
        id: this.id,
        username: username,
        user_id: userId,
        project_name_description: this.projectInfo.projectDescription,
        project_number: this.projectInfo.projectNumber,
        phase_id: this.projectInfo.phaseId,
        task_id: this.projectInfo.taskId,
        del_date: this.projectInfo.date,
        project_manager_id: this.projectInfo.projectManager.id,
        invoice_manager_id: this.projectInfo.invoiceManager.id,
        jsa_version: this.projectInfo.jsaVersion,
        // JSA/Waste Disposal backwards compatibility
        jsa_not_required: this.projectInfo.jsaAlreadyCompleted,
        jsa_already_completed: this.projectInfo.jsaAlreadyCompleted,
        waste_disposal_not_required:
          this.projectInfo.wasteDisposalAlreadyCompleted,
        waste_disposal_already_completed:
          this.projectInfo.wasteDisposalAlreadyCompleted,
        client_signatures_required: this.projectInfo.clientSignaturesRequired,
        submit: false,
        approved: false,
        approved_by: null,
        uploaded: false,
        rejected_reason: null,
      },
      // Adding the employees to the delData (skip rows without employee id for drafts)
      employees: this.employees
        .filter(
          (employee) =>
            employee.employeeId != null && employee.employeeId !== ""
        )
        .map((employee) => employee.toJson()),

      // Adding the units to the delData
      units: this.units
        .filter((unit) => unit.unitId != null && unit.unitId !== "")
        .map((unit) => unit.toJson()),
      // Adding the misc units to the delData
      misc_units: this.miscUnits
        .filter((unit) => unit.unitName != null && unit.unitName !== "")
        .map((unit) => unit.toJson()),
      // Adding the rental units to the delData
      misc_rentals: this.rentalUnits
        .filter((unit) => unit.itemName != null && unit.itemName !== "")
        .map((unit) => unit.toJson()),
      // Adding the project-specific units to the delData
      project_specific_units: this.projectSpecificUnits
        .filter((unit) => unit.unitNumber != null && unit.unitNumber !== "")
        .map((unit) => unit.toJson()),
      // Adding the field notes to the delData
      field_notes: this.fieldNotes.toJson(),
      // Adding the client signatures to the delData- taking this out due to saving/updating the client signatures when updated by themselves.
      client_signatures: this.clientSignatures.map((signature) =>
        signature.toJson()
      ),
      // Adding the JSA to the delData
      jsa: this.jsa.toJson(),
      // Adding the waste disposal to the delData
      waste_disposal: this.wasteDisposal.toJson(),
    };
    return delData;
  }

  submitDelData(delMode, userContext) {
    // Validating the del data to be submitted
    const delErrors = validateDelSubmitted(this, delMode);
    if (this.hasValidationErrors(delErrors)) {
      this.errors = delErrors;
      console.log("Del errors:", delErrors);
      return { errors: delErrors, displayMessages: collectValidationMessages(delErrors) };
    } else {
      this.errors = null;
    }
    // Getting user name and id from the provided user context
    const { username, userId } = this.getUserInfo(userContext);
    // Turning the del data into a json object
    const delData = {
      main_del_data: {
        id: this.id,
        username: username,
        user_id: userId,
        project_name_description: this.projectInfo.projectDescription,
        project_number: this.projectInfo.projectNumber,
        phase_id: this.projectInfo.phaseId,
        task_id: this.projectInfo.taskId,
        del_date: this.projectInfo.date,
        project_manager_id: this.projectInfo.projectManager.id,
        invoice_manager_id: this.projectInfo.invoiceManager.id,
        jsa_version: this.projectInfo.jsaVersion,
        // JSA/Waste Disposal backwards compatibility
        jsa_not_required: this.projectInfo.jsaAlreadyCompleted,
        jsa_already_completed: this.projectInfo.jsaAlreadyCompleted,
        waste_disposal_not_required:
          this.projectInfo.wasteDisposalAlreadyCompleted,
        waste_disposal_already_completed:
          this.projectInfo.wasteDisposalAlreadyCompleted,
        client_signatures_required: this.projectInfo.clientSignaturesRequired,
        submit: true,
        approved: false,
        approved_by: null,
        uploaded: false,
        rejected_reason: null,
      },
      // Adding the employees to the delData
      employees: this.employees.map((employee) => employee.toJson()),
      // Adding the units to the delData
      units: this.units.map((unit) => unit.toJson()),
      // Adding the misc units to the delData
      misc_units: this.miscUnits.map((unit) => unit.toJson()),
      // Adding the rental units to the delData
      misc_rentals: this.rentalUnits.map((unit) => unit.toJson()),
      // Adding the project-specific units to the delData
      project_specific_units: this.projectSpecificUnits.map((unit) => unit.toJson()),
      // Adding the field notes to the delData
      field_notes: this.fieldNotes.toJson(),
      // Adding the client signatures to the delData
      client_signatures: this.clientSignatures.map((signature) =>
        signature.toJson()
      ),
      // Adding the JSA to the delData
      jsa: this.jsa.toJson(),
      // Adding the waste disposal to the delData
      waste_disposal: this.wasteDisposal.toJson(),
    };
    return delData;
  }

  approveDelData(delMode, userContext) {
    // Validating the del data to be submitted
    const delErrors = validateDelSubmitted(this, delMode);
    if (this.hasValidationErrors(delErrors)) {
      this.errors = delErrors;
      console.log("Del errors:", delErrors);
      return { errors: delErrors, displayMessages: collectValidationMessages(delErrors) };
    } else {
      this.errors = null;
    }
    // Getting user name and id from the provided user context
    const { username, userId } = this.getUserInfo(userContext);
    // Turning the del data into a json object
    const delData = {
      main_del_data: {
        id: this.id,
        username: this.username,
        user_id: this.user_id,
        project_name_description: this.projectInfo.projectDescription,
        project_number: this.projectInfo.projectNumber,
        phase_id: this.projectInfo.phaseId,
        task_id: this.projectInfo.taskId,
        del_date: this.projectInfo.date,
        project_manager_id: this.projectInfo.projectManager.id,
        invoice_manager_id: this.projectInfo.invoiceManager.id,
        jsa_version: this.projectInfo.jsaVersion,
        // JSA/Waste Disposal backwards compatibility
        jsa_not_required: this.projectInfo.jsaAlreadyCompleted,
        jsa_already_completed: this.projectInfo.jsaAlreadyCompleted,
        waste_disposal_not_required:
          this.projectInfo.wasteDisposalAlreadyCompleted,
        waste_disposal_already_completed:
          this.projectInfo.wasteDisposalAlreadyCompleted,
        client_signatures_required: this.projectInfo.clientSignaturesRequired,
        submit: true,
        approved: true,
        approved_by: userId,
        uploaded: false,
        rejected_reason: null,
      },
      // Adding the employees to the delData
      employees: this.employees.map((employee) => employee.toJson()),
      // Adding the units to the delData
      units: this.units.map((unit) => unit.toJson()),
      // Adding the misc units to the delData
      misc_units: this.miscUnits.map((unit) => unit.toJson()),
      // Adding the rental units to the delData
      misc_rentals: this.rentalUnits.map((unit) => unit.toJson()),
      // Adding the project-specific units to the delData
      project_specific_units: this.projectSpecificUnits.map((unit) => unit.toJson()),
      // Adding the field notes to the delData
      field_notes: this.fieldNotes.toJson(),
      // Adding the client signatures to the delData
      client_signatures: this.clientSignatures.map((signature) =>
        signature.toJson()
      ),
      // Adding the JSA to the delData
      jsa: this.jsa.toJson(),
      // Adding the waste disposal to the delData
      waste_disposal: this.wasteDisposal.toJson(),
    };
    return delData;
  }

  /**
   * Validate DEL for submission and return minimal submit payload.
   * Data is already saved via WebSocket autosave, so we only need to send del_id.
   * @param {Object} userContext - The user context containing user info
   * @returns {{ del_id: string } | { errors: Object }}
   */
  submitDelSimple(userContext) {
    // Run frontend validation to show user-friendly errors immediately
    const delErrors = validateDelSubmitted(this, "edit");
    if (this.hasValidationErrors(delErrors)) {
      this.errors = delErrors;
      console.log("Del submit validation errors:", delErrors);
      return { errors: delErrors, displayMessages: collectValidationMessages(delErrors) };
    }
    this.errors = null;

    // Return minimal payload - backend validates and transitions state.
    // client_date is the user's local calendar day, used to stamp del_submit_date.
    return {
      del_id: this.id,
      client_date: getLocalDateString(),
    };
  }

  /**
   * Validate DEL for approval and return minimal approve payload.
   * Data is already saved via WebSocket autosave, so we only need to send del_id.
   * @param {Object} userContext - The user context containing user info
   * @returns {{ del_id: string } | { errors: Object }}
   */
  approveDelSimple(userContext) {
    // Run frontend validation
    const delErrors = validateDelSubmitted(this, "approve");
    if (this.hasValidationErrors(delErrors)) {
      this.errors = delErrors;
      console.log("Del approve validation errors:", delErrors);
      return { errors: delErrors, displayMessages: collectValidationMessages(delErrors) };
    }
    this.errors = null;

    // Return minimal payload - backend validates and transitions state.
    // client_date is the user's local calendar day, used to stamp del_approval_date.
    return {
      del_id: this.id,
      client_date: getLocalDateString(),
    };
  }
}
